import { useMemo, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { joiResolver } from '@hookform/resolvers/joi';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import CountryFlag from 'components/atoms/CountryFlag';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import AddressSearchField from 'components/atoms/Form/AddressSearchField';
import { Select } from 'components/atoms/Form/Select';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useSettingsSelectOptions from 'hooks/useSettingsSelectOptions';
import { AccountSettingsFieldsNamesEnum } from 'types/settings.types';
import { AvailableCountries, type IUser } from 'types/users.types';
import { constructAccountDetailsFormDefaultValues } from 'utils/settings/settingsHelpers';
import { getSelectOptionValue } from 'utils/helpers';
import type { PlacesAutocompleteIncludedPrimaryType } from 'hooks/shared/useGoogleAddressAutocomplete';
import type { BankingSignupAccountDetails } from '../constants/onboarding.types';
import type { ActivationStepProps } from '../../ActivationFlow/types';
import {
  BEBusinessAddressSchema,
  DEBusinessAddressSchema,
} from '../../utils/validation';
import { useKBOAddress } from '../hooks/useKBOAddress';

const ADDRESS_TYPES: readonly PlacesAutocompleteIncludedPrimaryType[] = [
  'geocode',
] as const;

interface BusinessAddressStepProps extends ActivationStepProps {
  currentAccountDetailsValues?: Partial<BankingSignupAccountDetails>;
  onConfirm: (values: BankingSignupAccountDetails) => void;
  isConfirming?: boolean;
}

const BusinessAddressStep: FC<BusinessAddressStepProps> = ({
  onConfirm,
  currentAccountDetailsValues,
  isConfirming,
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  showBackButton,
  onBack,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();
  const user = useGetCurrentCustomerData().data as IUser;
  const isBelgium = user?.country === AvailableCountries.belgium;

  const defaultValues = useMemo(
    () =>
      constructAccountDetailsFormDefaultValues(
        (currentAccountDetailsValues ?? user) as IUser,
      ),
    [currentAccountDetailsValues, user],
  );

  const schema = isBelgium
    ? BEBusinessAddressSchema()
    : DEBusinessAddressSchema();

  const methods = useForm({
    defaultValues,
    resolver: joiResolver(schema, {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  const setAndValidate: typeof methods.setValue = (name, value, options) =>
    methods.setValue(name, value, { shouldValidate: true, ...options });

  const settingsOptions = useSettingsSelectOptions(
    user?.country as AvailableCountries,
  );

  const [addressExpanded, setAddressExpanded] = useState(
    // we never reach collapse state in BE, so we always show the address fields
    () => isBelgium || !!currentAccountDetailsValues?._addressSearchCompleted,
  );

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton={showBackButton}
      onBack={onBack}
    >
      <FormProvider {...methods}>
        <form
          className="w-[80%] mx-auto"
          onSubmit={methods.handleSubmit(() =>
            onConfirm({
              ...methods.getValues(),
              _addressSearchCompleted: true,
            }),
          )}
        >
          {isBelgium ? (
            <BEAddressFields
              methods={methods}
              settingsOptions={settingsOptions}
              setAndValidate={setAndValidate}
              addressExpanded={addressExpanded}
              setAddressExpanded={setAddressExpanded}
            />
          ) : (
            <DEAddressFields
              methods={methods}
              settingsOptions={settingsOptions}
              setAndValidate={setAndValidate}
              addressExpanded={addressExpanded}
              setAddressExpanded={setAddressExpanded}
            />
          )}

          <div className="text-center">
            <Button
              type="submit"
              color="bank"
              className="my-6 mt-12 w-full capitalize"
              loading={isConfirming}
              disabled={isUpdatingStep || !addressExpanded}
            >
              {t('continue')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </OnboardingStepWrapper>
  );
};

// ===========================
// BELGIUM ADDRESS FIELDS
// ===========================

const BEAddressFields: FC<{
  methods: ReturnType<typeof useForm>;
  settingsOptions: ReturnType<typeof useSettingsSelectOptions>;
  setAndValidate: (name: any, value: any, options?: any) => void;
  addressExpanded: boolean;
  setAddressExpanded: (value: boolean) => void;
}> = ({
  methods,
  settingsOptions,
  setAndValidate,
  addressExpanded,
  setAddressExpanded,
}) => {
  const { t } = useTranslation();

  const [streetValue, cityValue, zipValue, VATNumberValue] = methods.watch([
    AccountSettingsFieldsNamesEnum.street,
    AccountSettingsFieldsNamesEnum.city,
    AccountSettingsFieldsNamesEnum.zip,
    AccountSettingsFieldsNamesEnum.VATNumber,
  ]);

  const { kboAddressMismatch } = useKBOAddress({
    vatNumber: VATNumberValue,
    streetValue,
    cityValue,
    zipValue,
    setValue: methods.setValue,
    setError: methods.setError,
    clearErrors: methods.clearErrors,
    skipPrefill: true,
  });

  return (
    <AddressSearchField
      label={t('address.enter_your_business_address')}
      searchPlaceholder={t('search')}
      countryRestrictions={[AvailableCountries.belgium]}
      includedPrimaryTypes={ADDRESS_TYPES}
      isExpanded={addressExpanded}
      onManualEntry={() => setAddressExpanded(true)}
      onAddressSelect={(address) => {
        setAndValidate(AccountSettingsFieldsNamesEnum.street, address.street, {
          shouldDirty: true,
        });
        setAndValidate(AccountSettingsFieldsNamesEnum.city, address.city, {
          shouldDirty: true,
        });
        setAndValidate(AccountSettingsFieldsNamesEnum.zip, address.zip, {
          shouldDirty: true,
        });
        setAddressExpanded(true);
      }}
    >
      <h3 className="mb-1 mt-3 text-primary font-semibold">
        {t('accountable_banking.onboarding.form.address.heading')}
      </h3>

      <div className="flex space-x-3">
        <FieldConnector
          name={AccountSettingsFieldsNamesEnum.country}
          className="flex-1"
        >
          {({ field, name, label }) => (
            <Select
              name={name}
              isDisabled
              value={
                getSelectOptionValue(
                  settingsOptions.countriesOptions,
                  field.value,
                )?.value
              }
              formatOptionLabel={(data) => (
                <div className="flex items-center">
                  <CountryFlag country={data.value} className="h-6 w-6" />
                  <p className="ml-4">{data.label}</p>
                </div>
              )}
              placeholder={label}
              options={settingsOptions.countriesOptions}
              onChange={(value) => field.onChange(value)}
            />
          )}
        </FieldConnector>

        <FieldConnector
          className="flex-1"
          name={AccountSettingsFieldsNamesEnum.street}
        >
          {({ field }) => (
            <FreeTextInput
              {...field}
              placeholder={t('settings.companyDetails.street')}
            />
          )}
        </FieldConnector>
      </div>

      <div className="flex space-x-3 mt-3">
        <FieldConnector
          className="flex-1"
          name={AccountSettingsFieldsNamesEnum.zip}
        >
          {({ field }) => (
            <FreeTextInput
              {...field}
              placeholder={t('settings.companyDetails.postalCode')}
            />
          )}
        </FieldConnector>

        <FieldConnector
          className="flex-1"
          name={AccountSettingsFieldsNamesEnum.city}
        >
          {({ field }) => (
            <FreeTextInput
              {...field}
              placeholder={t('settings.companyDetails.city')}
            />
          )}
        </FieldConnector>
      </div>

      {kboAddressMismatch && (
        <Alert
          className="mt-3"
          type="error"
          description={t('accountable_banking.onboarding.form.be.kbo.content')}
        />
      )}
    </AddressSearchField>
  );
};

// ===========================
// GERMANY ADDRESS FIELDS
// ===========================

const DEAddressFields: FC<{
  methods: ReturnType<typeof useForm>;
  settingsOptions: ReturnType<typeof useSettingsSelectOptions>;
  setAndValidate: (name: any, value: any, options?: any) => void;
  addressExpanded: boolean;
  setAddressExpanded: (value: boolean) => void;
}> = ({
  methods,
  settingsOptions,
  setAndValidate,
  addressExpanded,
  setAddressExpanded,
}) => {
  const { t } = useTranslation();

  return (
    <AddressSearchField
      label={t('address.enter_your_business_address')}
      searchPlaceholder={t('search')}
      countryRestrictions={[AvailableCountries.germany]}
      includedPrimaryTypes={ADDRESS_TYPES}
      isExpanded={addressExpanded}
      onManualEntry={() => setAddressExpanded(true)}
      onAddressSelect={(address) => {
        setAndValidate(AccountSettingsFieldsNamesEnum.street, address.street, {
          shouldDirty: true,
        });
        setAndValidate(AccountSettingsFieldsNamesEnum.city, address.city, {
          shouldDirty: true,
        });
        setAndValidate(AccountSettingsFieldsNamesEnum.zip, address.zip, {
          shouldDirty: true,
        });
        if (address.state) {
          setAndValidate(AccountSettingsFieldsNamesEnum.state, address.state, {
            shouldDirty: true,
          });
        }
        setAddressExpanded(true);
      }}
    >
      <h3 className="mb-1 mt-3 text-primary font-semibold">
        {t('accountable_banking.onboarding.form.address.heading')}
      </h3>

      <FieldConnector name={AccountSettingsFieldsNamesEnum.country}>
        {({ field, name, label }) => (
          <Select
            name={name}
            isDisabled
            value={
              getSelectOptionValue(
                settingsOptions.countriesOptions,
                field.value,
              )?.value
            }
            formatOptionLabel={(data) => (
              <div className="flex items-center">
                <CountryFlag country={data.value} className="h-6 w-6" />
                <p className="ml-4">{data.label}</p>
              </div>
            )}
            placeholder={label}
            options={settingsOptions.countriesOptions}
            onChange={(value) => field.onChange(value)}
          />
        )}
      </FieldConnector>

      <div className="flex items-start my-3 space-x-3">
        <FieldConnector
          className="flex-1"
          name={AccountSettingsFieldsNamesEnum.street}
        >
          {({ field }) => (
            <FreeTextInput
              {...field}
              placeholder={t('settings.companyDetails.street')}
            />
          )}
        </FieldConnector>

        <FieldConnector
          className="flex-1"
          name={AccountSettingsFieldsNamesEnum.zip}
        >
          {({ field }) => (
            <FreeTextInput
              {...field}
              placeholder={t('settings.companyDetails.postalCode')}
            />
          )}
        </FieldConnector>
      </div>

      <div className="flex items-start my-3 space-x-3">
        <FieldConnector
          className="flex-1"
          name={AccountSettingsFieldsNamesEnum.state}
        >
          {({ name, field }) => (
            <Select
              {...field}
              name={name}
              placeholder={t('settings.companyDetails.state')}
              options={settingsOptions.germanStatesOptions}
              value={
                getSelectOptionValue(
                  settingsOptions.germanStatesOptions,
                  field.value,
                )?.value
              }
              onChange={({ value }: any) => {
                field.onChange(value);
                methods.clearErrors(name as any);
              }}
            />
          )}
        </FieldConnector>

        <FieldConnector
          className="flex-1"
          name={AccountSettingsFieldsNamesEnum.city}
        >
          {({ field }) => (
            <FreeTextInput
              {...field}
              placeholder={t('settings.companyDetails.city')}
            />
          )}
        </FieldConnector>
      </div>

      <Alert
        className="mt-3"
        type="info"
        description={t(
          'accountable_banking.onboarding.form.bussiness_registry_address_banner.content',
        )}
      />
    </AddressSearchField>
  );
};

export default BusinessAddressStep;
